import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { isMobile, isCmApprover, isDmApprover, isAllApprover } from "utils/common";
import Button from "react-bootstrap/Button";
import AccountLookup from "common/accountLookup";
import { getJobDetailsService } from "services/priceQuote.service";
import MultiSelectInput from "common/multiSelectInput";
import { showSpinner, hideSpinner } from "store/slices/spinner";
import {
  getDistrictOptions,
  getStoreNoOptions,
  getlaborCodeOptions,
  getstatusOptions,
  updateSearchFields,
  clearCustomerFields,
  updateSearchFilterValue,
  searchSubmit,
} from "store/laborTable/actionCreator";
import sortBy from "lodash/sortBy";
import { toast } from "react-toastify";
import "./style.scss";

const SearchFields = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const districtNode = useRef();
  const storeNode = useRef();
  const laborCodeNode = useRef();
  const statusNode = useRef();
  const [mobileView, setMobileView] = useState(false);
  const [showStoreBtn, setShowStoreBtn] = useState(true);
  const { fcRole, storeCcn } = useSelector((state) => state.loggedInUser);
  const { districtList, storeNumberList, laborCodeList, statusList, tableSearchFields } = useSelector(
    (state) => state.laborTable,
  );
  const [jobList, setJobList] = useState([]);
  useEffect(() => {
    dispatch(getDistrictOptions());
    dispatch(getStoreNoOptions());
    dispatch(getlaborCodeOptions());
    dispatch(getstatusOptions());
  }, []);

  useEffect(() => {
    if (tableSearchFields.accountNumber) {
      dispatch(showSpinner());
      getJobDetailsService(tableSearchFields.accountNumber)
        .then((res) => {
          if (res.length) {
            setJobList(res);
          } else {
            setJobList([]);
          }
          let tempObj = {
            id: "",
            label: "",
            description: "",
          };
          dispatch(updateSearchFields("job", tempObj));
          dispatch(hideSpinner());
        })
        .catch((err) => {
          console.log("err", err);
          dispatch(hideSpinner());
        });
    }
  }, [tableSearchFields.accountNumber]);

  useEffect(() => {
    storeNumberList?.some((item) => {
      if (item.id === storeCcn) {
        setShowStoreBtn(true);
      } else {
        setShowStoreBtn(false);
      }
      return item.id === storeCcn;
    });
  }, [storeNumberList]);

  useEffect(() => {
    const handleResize = () => {
      if (!isMobile()) {
        setMobileView(false);
      } else {
        setMobileView(true);
      }
    };
    window.addEventListener("resize", handleResize);
    if (isMobile()) {
      handleResize();
    }
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [mobileView]);

  const handleDistrictValues = (e, value) => {
    const selectedDistricts = [...tableSearchFields.district];
    selectedDistricts.includes(value)
      ? selectedDistricts.splice(selectedDistricts.indexOf(value), 1)
      : selectedDistricts.push(value);
    const key = "district";
    dispatch(updateSearchFields(key, selectedDistricts));
  };

  const handleStoreNoValues = (e, value) => {
    const selectedStoreNumber = [...tableSearchFields.storeNumber];
    selectedStoreNumber.includes(value)
      ? selectedStoreNumber.splice(selectedStoreNumber.indexOf(value), 1)
      : selectedStoreNumber.push(value);
    const key = "storeNumber";
    dispatch(updateSearchFields(key, selectedStoreNumber));
  };

  const handleLaborCodeValues = (e, value) => {
    const selectedLaborCode = [...tableSearchFields.laborCode];
    selectedLaborCode.includes(value)
      ? selectedLaborCode.splice(selectedLaborCode.indexOf(value), 1)
      : selectedLaborCode.push(value);
    const key = "laborCode";
    dispatch(updateSearchFields(key, selectedLaborCode));
  };

  const handleStatusValues = (e, value) => {
    const selectedStatus = [...tableSearchFields.status];
    selectedStatus.includes(value)
      ? selectedStatus.splice(selectedStatus.indexOf(value), 1)
      : selectedStatus.push(value);
    const key = "status";
    dispatch(updateSearchFields(key, selectedStatus));
  };

  const handleClear = () => {
    dispatch(clearCustomerFields());
  };

  const handleSearch = () => {
    dispatch(updateSearchFilterValue(""));
    const payload = {
      district: tableSearchFields.district,
      storeNumber: tableSearchFields.storeNumber,
      laborCode: tableSearchFields.laborCode,
      status: tableSearchFields.status,
      accountNumber: tableSearchFields.accountNumber ? [tableSearchFields.accountNumber] : [],
      jobNumber:
        (tableSearchFields.job.id && tableSearchFields.job.id.length > 3) || !tableSearchFields.job.id
          ? []
          : [tableSearchFields.job.id.toString()],
      parentNumber: tableSearchFields.parentNumber,
      priceType: tableSearchFields.priceType.label ? [tableSearchFields.priceType.label] : ["0", "1"],
    };

    dispatch(searchSubmit(payload)).catch((err) => toast.error("Error while fetching results, please try again."));
  };

  const handleViewStore = () => {
    dispatch(clearCustomerFields());
    dispatch(updateSearchFields("storeNumber", [storeCcn]));
    const payload = {
      district: [],
      storeNumber: [storeCcn],
      laborCode: [],
      status: [],
      accountNumber: [],
      jobNumber: [],
      parentNumber: "",
      priceType: [],
    };
    dispatch(searchSubmit(payload)).catch((err) => toast.error("Error while fetching results, please try again."));
  };

  const handleExceptionPricing = () => {
    navigate("/laborTable/expectionPricing");
  };

  const handleJob = (e, value) => {
    let tempObj = {
      id: value.jobNbr,
      label: value.jobNbr,
      description: !value.jobName ? "" : value.jobName,
    };
    dispatch(updateSearchFields("job", tempObj));
  };

  return (
    <div className="container-fluid search-form-container">
      <div className="search-form-container__title d-flex justify-content-between flex-column flex-md-row align-items-md-center">
        <span className="search-form-container__title--text mb-4 mb-md-0">Labor Table</span>
        <div className="d-flex align-items-center">
          {showStoreBtn && (
            <Button variant="outline-primary" onClick={handleViewStore}>
              View My Store
            </Button>
          )}
          {!(isCmApprover(fcRole) || isDmApprover(fcRole) || isAllApprover(fcRole)) && (
            <Button variant="outline-primary" onClick={handleExceptionPricing}>
              Exception Pricing
            </Button>
          )}
        </div>
      </div>
      <div className="search-form-container__fields">
        <form className="search-form-container__fields__form">
          <div className="row">
            <div className="col-12 col-sm-12 col-md-6 col-lg-3 search-form-container__fields__form-group">
              <MultiSelectInput
                inputRef={districtNode}
                options={sortBy(
                  districtList.map(({ id, description }) => ({
                    id,
                    // for backwards compatibility. Before a corresponding backend change description used to match id.
                    description: description === id ? id : `${id} - ${description}`,
                  })),
                  "description",
                )}
                label="District"
                defaultValue="Select any district"
                handleSelectInput={handleDistrictValues}
                selectedValues={tableSearchFields.district}
              />
            </div>
            <div className="col-12 col-sm-12 col-md-6 col-lg-3 search-form-container__fields__form-group">
              <MultiSelectInput
                inputRef={storeNode}
                options={storeNumberList}
                label="Store Number"
                defaultValue="Select any store number"
                handleSelectInput={handleStoreNoValues}
                selectedValues={tableSearchFields.storeNumber}
                isStoreOptions={true}
              />
            </div>
            <div className="col-12 col-sm-12 col-md-6 col-lg-3 search-form-container__fields__form-group">
              <MultiSelectInput
                inputRef={laborCodeNode}
                options={laborCodeList}
                isLaborOptions={true}
                label="Labor Code"
                defaultValue="Select any labor code"
                handleSelectInput={handleLaborCodeValues}
                selectedValues={tableSearchFields.laborCode}
              />
            </div>
            <div className="col-12 col-sm-12 col-md-6 col-lg-3 search-form-container__fields__form-group">
              <MultiSelectInput
                inputRef={statusNode}
                options={statusList}
                label="Status"
                defaultValue="Select a status"
                handleSelectInput={handleStatusValues}
                selectedValues={tableSearchFields.status}
              />
            </div>
          </div>
          <div className="row">
            <AccountLookup
              numberFieldChangeCallback={(accNo) => dispatch(updateSearchFields("accountNumber", accNo))}
              nameFieldChangeCallback={(accName) => dispatch(updateSearchFields("accountName", accName))}
              numberFieldWrapperClass="col-12 col-sm-12 col-md-6 col-lg-2 search-form-container__fields__form-group"
              nameFieldWrapperClass="col-12 col-sm-12 col-md-6 col-lg-3 search-form-container__fields__form-group"
              isLaborSearch={true}
              handleJob={handleJob}
              jobList={jobList}
              initialAccountNumber={tableSearchFields.accountNumber}
              initialAccountName={tableSearchFields.accountName}
              tableSearchFields={tableSearchFields}
            />
          </div>
        </form>
        <div className="d-flex search-form-container__fields__btn">
          <Button variant="primary" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="outline-primary" onClick={handleClear}>
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SearchFields;
